import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Instagram, Twitter, Music2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import ShareButton from "../components/ShareButton";
import { api } from "../lib/api";

export default function ArtistPage() {
    const { id } = useParams();
    const [artist, setArtist] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/artists/${id}`)
            .then((r) => setArtist(r.data))
            .catch(() => setError("Artist not found"));
    }, [id]);

    if (error)
        return (
            <div className="bg-black text-white min-h-screen">
                <Header />
                <main className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-streetz-muted">{error}</main>
                <Footer />
            </div>
        );
    if (!artist) return null;

    return (
        <div className="bg-black text-white min-h-screen">
            <SEO
                title={artist.artist_name}
                description={artist.ai_review?.verdict || artist.bio?.slice(0, 160)}
                image={artist.photo_url}
                type="profile"
                keywords={[artist.artist_name, artist.genre, artist.location, "Afrobeats", "MyStreetzMag"]}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "MusicGroup",
                    name: artist.artist_name,
                    genre: artist.genre,
                    description: artist.bio,
                    image: artist.photo_url || undefined,
                    url: typeof window !== "undefined" ? window.location.href : undefined,
                }}
            />
            <Header />
            <main className="max-w-4xl mx-auto px-4 md:px-8 py-12">
                <Link to="/" data-testid="artist-back-link" className="inline-flex items-center gap-2 overline text-streetz-muted hover:text-streetz-red">
                    <ArrowLeft className="w-4 h-4" /> BACK
                </Link>

                <article data-testid="artist-profile" className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-1">
                        {artist.photo_url ? (
                            <img src={artist.photo_url} alt={artist.artist_name} className="w-full aspect-square object-cover border border-streetz-line" />
                        ) : (
                            <div className="w-full aspect-square bg-streetz-paper border border-streetz-line grid place-items-center font-display text-7xl text-streetz-line">
                                {artist.artist_name?.[0]}
                            </div>
                        )}
                    </div>
                    <div className="md:col-span-2 flex flex-col gap-3">
                        <span className="overline text-streetz-yellow">{artist.genre} · {artist.location}</span>
                        <h1 className="font-display uppercase text-5xl md:text-7xl leading-none">{artist.artist_name}</h1>
                        {artist.ai_review?.profile && (
                            <p className="text-white/85 leading-relaxed mt-2 border-l-2 border-streetz-red pl-4">{artist.ai_review.profile}</p>
                        )}
                        <p className="text-streetz-muted">{artist.bio}</p>

                        <div className="flex flex-wrap gap-3 mt-2">
                            <a href={artist.song_url} target="_blank" rel="noopener noreferrer" data-testid="artist-listen-link" className="bg-streetz-red text-white overline px-4 py-2 inline-flex items-center gap-2 hover:bg-white hover:text-black">
                                <Music2 className="w-4 h-4" /> LISTEN
                            </a>
                            {artist.socials?.instagram && (
                                <a href={`https://instagram.com/${artist.socials.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="overline border border-streetz-line px-3 py-2 inline-flex items-center gap-2 hover:border-streetz-red">
                                    <Instagram className="w-4 h-4" /> INSTAGRAM
                                </a>
                            )}
                            {artist.socials?.twitter && (
                                <a href={`https://twitter.com/${artist.socials.twitter.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="overline border border-streetz-line px-3 py-2 inline-flex items-center gap-2 hover:border-streetz-red">
                                    <Twitter className="w-4 h-4" /> TWITTER
                                </a>
                            )}
                        </div>

                        {artist.ai_review?.promo_caption && (
                            <div className="mt-6 border-t border-streetz-line pt-4">
                                <div className="overline text-streetz-muted">SUGGESTED CAPTION</div>
                                <p className="text-white mt-1">{artist.ai_review.promo_caption}</p>
                            </div>
                        )}

                        <div className="mt-6 border-t border-streetz-line pt-4">
                            <ShareButton
                                title={`${artist.artist_name} on MyStreetzMag`}
                                text={artist.ai_review?.verdict || artist.bio?.slice(0, 160) || ""}
                                testId="artist-share-button"
                            />
                        </div>
                    </div>
                </article>
            </main>
            <Footer />
        </div>
    );
}
